import {
	UnifiedApplication,
	UnifiedApplicationChild,
	UnifiedApplicationChildType,
	UnifiedApplicationStatus,
} from "../../resources/unifiedApplication"
import {StatusBannerTone} from "../../components/StatusBanner/StatusBanner"

// All user-facing wording for the Unified Application page lives here, so product can change it in one place.

export type StatusOwner = "End user" | "Unit" | "Unit compliance" | "Stripe" | "Support or engineering" | "Nobody"

export type StatusBannerContent = {
	tone: StatusBannerTone
	headline: string
	lines: string[]
}

export function getChildLabel(child: UnifiedApplicationChild): string {
	return child.attributes.type === UnifiedApplicationChildType.Unit ? "Unit application" : "Processor application"
}

export function getChildOwner(child: UnifiedApplicationChild): StatusOwner {
	switch (child.attributes.status) {
		case UnifiedApplicationStatus.Pending:
			return child.attributes.type === UnifiedApplicationChildType.Unit ? "Unit" : "Stripe"
		case UnifiedApplicationStatus.AwaitingDocuments:
		case UnifiedApplicationStatus.AwaitingIdentityVerification:
			return "End user"
		case UnifiedApplicationStatus.Submitted:
		case UnifiedApplicationStatus.InReview:
			return child.attributes.type === UnifiedApplicationChildType.Unit ? "Unit compliance" : "Stripe"
		case UnifiedApplicationStatus.Error:
			return "Support or engineering"
		case UnifiedApplicationStatus.Approved:
		case UnifiedApplicationStatus.Rejected:
		case UnifiedApplicationStatus.Cancelled:
			return "Nobody"
	}
}

export function getChildLine(child: UnifiedApplicationChild): string {
	switch (child.attributes.status) {
		case UnifiedApplicationStatus.Pending:
			return child.attributes.type === UnifiedApplicationChildType.Unit
				? "Being evaluated by Unit."
				: "Waiting for Stripe to finish setting up the account."
		case UnifiedApplicationStatus.AwaitingDocuments:
			return "Needs documents from the end user."
		case UnifiedApplicationStatus.AwaitingIdentityVerification:
			return child.attributes.type === UnifiedApplicationChildType.Unit
				? "Unit needs the end user to finish identity verification."
				: "Stripe needs the end user to finish identity verification."
		case UnifiedApplicationStatus.Submitted:
			return "Submitted, waiting to be reviewed."
		case UnifiedApplicationStatus.InReview:
			return "Being reviewed."
		case UnifiedApplicationStatus.Approved:
			return child.attributes.type === UnifiedApplicationChildType.Unit
				? "Approved, customer and account created."
				: "Payments enabled on Stripe."
		case UnifiedApplicationStatus.Rejected:
			return "Declined."
		case UnifiedApplicationStatus.Error:
			return "Hit an error and won't move on its own."
		case UnifiedApplicationStatus.Cancelled:
			return "Cancelled."
	}
}

function isWaiting(child: UnifiedApplicationChild) {
	const owner = getChildOwner(child)
	return owner !== "Nobody" && owner !== "Support or engineering"
}

export function getStatusBanner(application: UnifiedApplication): StatusBannerContent {
	const {status, children} = application.attributes

	switch (status) {
		case UnifiedApplicationStatus.Approved:
			return {
				tone: "success",
				headline: "Done: the Unit application and the processor application are both approved.",
				lines: [],
			}
		case UnifiedApplicationStatus.PartiallyApproved: {
			const declined = children.filter((child) => child.attributes.status === UnifiedApplicationStatus.Rejected)
			return {
				tone: "warning",
				headline: "Partially approved: one application was declined.",
				lines: declined.length > 0 ? [`Declined: ${declined.map(getChildLabel).join(", ")}.`] : [],
			}
		}
		case UnifiedApplicationStatus.Rejected:
			return {tone: "error", headline: "Rejected: both applications were declined.", lines: []}
		case UnifiedApplicationStatus.Error:
			return {
				tone: "error",
				headline: "Stuck: needs support or engineering.",
				lines: ["One application is in Error and won't recover on its own. Escalate with the raw data below."],
			}
		case UnifiedApplicationStatus.Cancelled:
			return {tone: "neutral", headline: "Cancelled.", lines: []}
		default: {
			const waiting = children.filter(isWaiting)
			const owners = Array.from(new Set(waiting.map(getChildOwner)))
			return {
				tone: "info",
				headline: owners.length > 0 ? `Waiting on: ${owners.join(", ")}` : "Waiting for updates",
				lines: waiting.map((child) => `${getChildLabel(child)}: ${getChildLine(child)}`),
			}
		}
	}
}
